// The shapes that cross the boundary when somebody signs up, signs in, or is
// identified on a request. The normative schema for ICD-S03 Identity & Session.
//
// WHY THESE ARE HERE AND NOT IN THE SERVER. Both ends parse them: the client
// parses what comes back and the server parses what comes in, which is the
// rule in API.md and the reason a shape mismatch is a loud error rather than a
// blank screen. A schema that lives in the server can only be enforced at one
// end.
//
// WHAT IS DELIBERATELY ABSENT. No password hash, no session token, no reset
// token in any response shape. A token exists in exactly two places — the
// cookie on the wire and its SHA-256 in `sessions.id` — and it is never a
// field in a view, because a field in a view ends up in a log. ICD-S03 §7.
//
// THE ONE EXCEPTION, decided 2026-09-17 (A9) and deliberately narrow: a
// client that says `native: true` when it signs in is saying it has no cookie
// jar, and `MeView.session.token` is then the only way it can ever hold a
// session. It is set on that one response, to that one caller, and never on
// any other route — `GET /api/me` carries an expiry and no token, however the
// caller authenticated. A browser never asks for it and never receives it.
//
// IDENTITY IS AN ID (docs/decisions/2026-09-10-identity-is-an-id.md). Nothing
// here treats an email as a key. `email` is an input to finding a login row,
// never an identifier that anything else references.

import { z } from 'zod';
import { DeviceForm } from './activity';
import { TimeZone } from '../time/zone';
import { AvatarRef } from './views';
import {
  COMMON_PASSWORD_MESSAGE,
  EMAIL_PASSWORD_MESSAGE,
  isCommonPassword,
  isEmailPassword,
} from './passwords';

/**
 * Length, and not one of the most common (`NewPassword`); no composition rules
 * (ACCOUNTS.md). Composition rules push people toward `Passw0rd!` and buy
 * nothing. Eight since 2026-10-01, when Ken lowered it from twelve: an account
 * here holds nothing of value (B-231).
 */
export const PASSWORD_MIN_LENGTH = 8;

/** A password as typed. Never stored, never logged, never returned. */
export const Password = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
  // An upper bound is not a policy, it is a denial-of-service guard: the hash
  // is deliberately slow, so an unbounded input is an unbounded amount of CPU.
  .max(256);

/**
 * A password being set — signing up, a reset, a change, an email change's
 * undo — and so not one of the most common (`passwords.ts`, DIRECTION A27).
 * Signing in takes `Password`: an account made before the rule still opens.
 */
export const NewPassword = Password.refine((p) => !isCommonPassword(p), {
  message: COMMON_PASSWORD_MESSAGE,
});

/**
 * An email as typed. Compared case-insensitively, because `user_logins` is
 * `COLLATE NOCASE` on `identifier` and the two must agree or a second account
 * can be minted with the same address in different case.
 */
export const Email = z.string().trim().min(3).max(254).email();

/** How a person signs in. One row each in `user_logins`; a user may hold several. */
export const LoginKind = z.enum(['password', 'magic-link', 'google', 'apple']);
export type LoginKind = z.infer<typeof LoginKind>;

/**
 * What a client tells us about itself: mostly a non-browser one, and the one
 * thing a browser's agent gets wrong (`form`, an iPad's Safari saying Mac).
 *
 * Both sign-in shapes carry these, and all three are optional or default to
 * the browser answer, so a client that says nothing is treated exactly as
 * before.
 */
const ClientKind = {
  /**
   * "I have no cookie jar." A native client reads its session token out of
   * the response body and sends it back as `Authorization: Bearer`. Saying
   * so is what unlocks the token; a browser must never set it, because a
   * token in a body is a token in a log, and the cookie it already gets is
   * `HttpOnly` precisely so the page cannot read it.
   */
  native: z.boolean().default(false),
  /**
   * The label for the account page's device list — "Ken's iPhone".
   *
   * A browser's label is derived from its user agent, which a native client
   * does not meaningfully have. Capped and trimmed because it is rendered:
   * this is the one string on this interface that somebody else's client
   * chooses and a person reads.
   */
  device: z.string().trim().min(1).max(40).optional(),
  /**
   * Phone, tablet or computer, for a client that knows better than its user
   * agent. The server reads the agent when this is absent, and an iPad's
   * Safari says it is a Mac (the activity log's `session.started`, A2).
   */
  form: DeviceForm.optional(),
};

/** The three fields any caller may add, read before the rest of the body parses. */
export const ClientHints = z.object(ClientKind);
export type ClientHints = z.infer<typeof ClientHints>;

export const SignupRequest = z
  .object({
    displayName: z.string().trim().min(1).max(40),
    email: Email,
    password: NewPassword,
    /**
     * The zone every time this person reads is told in, chosen when the account
     * is made (B-181). Required: an account made without one would read times in
     * whichever league it opened, which is the thing the field exists to end.
     */
    timeZone: TimeZone,
    ...ClientKind,
  })
  // Not the address it is made with, the one word a stranger already has.
  .refine((b) => !isEmailPassword(b.password, b.email), {
    path: ['password'],
    message: EMAIL_PASSWORD_MESSAGE,
  });
export type SignupRequest = z.infer<typeof SignupRequest>;
/** What a caller hands in, before parsing. */
export type SignupInput = z.input<typeof SignupRequest>;

export const LoginRequest = z.object({
  email: Email,
  password: Password,
  /** "Remember this device": 90 days rather than 14 (ACCOUNTS.md). */
  remember: z.boolean().default(false),
  ...ClientKind,
});
export type LoginRequest = z.infer<typeof LoginRequest>;
/**
 * What a caller hands in. Distinct from the inferred type because
 * `remember` is defaulted: after parsing it is always present, before
 * parsing it is optional, and an operation signature that demands the
 * parsed shape makes every call site supply a value the schema exists to
 * supply for them.
 */
export type LoginInput = z.input<typeof LoginRequest>;

export const ResetRequest = z.object({
  email: Email,
});
export type ResetRequest = z.infer<typeof ResetRequest>;
export type ResetInput = z.input<typeof ResetRequest>;

export const ResetConfirm = z.object({
  /** The opaque token from the email. Its SHA-256 is the `password_resets` key. */
  token: z.string().min(1),
  password: NewPassword,
  // Using the link signs the device in, as sign-in does, so a native client
  // asks for its token the same way (A9).
  ...ClientKind,
});
export type ResetConfirm = z.infer<typeof ResetConfirm>;
export type ResetConfirmInput = z.input<typeof ResetConfirm>;

/**
 * A link that confirms an address is the person's (`POST /api/auth/email/confirm`,
 * PLAN-2026-10-18-families-and-newcomers §7.1). Needs no session: the link may
 * be opened on a device that is not signed in.
 */
export const EmailConfirm = z.object({
  /** The opaque token from the email. Its SHA-256 is the `email_confirmations` key. */
  token: z.string().min(1).max(200),
});
export type EmailConfirm = z.infer<typeof EmailConfirm>;
export type EmailConfirmInput = z.input<typeof EmailConfirm>;

/**
 * Ask for a sign-in code by email (`POST /api/auth/magic/request`,
 * PLAN-2026-10-28-by-email §3.4): the address. The answer is the same for
 * every address, as a reset's is.
 */
export const SignInCodeRequest = z.object({
  email: Email,
});
export type SignInCodeRequest = z.infer<typeof SignInCodeRequest>;
export type SignInCodeInput = z.input<typeof SignInCodeRequest>;

/**
 * Sign in with what the email carried (`POST /api/auth/magic/confirm`): the
 * link's token, or the address and the six digits typed where the code was
 * asked for. The code may be typed with a space or a dash in it (*482 913*);
 * the store reads the digits. Signs this device in, as sign-in does, so a
 * native client asks for its token the same way (A9).
 */
export const SignInCodeConfirm = z.union([
  z.object({
    /** The opaque token from the email's link. Its SHA-256 is the `sign_in_codes` key. */
    token: z.string().min(16).max(200),
    ...ClientKind,
  }),
  z.object({
    email: Email,
    /** Six digits, as typed. */
    code: z.string().trim().min(6).max(12),
    /** "Remember this device", from the sheet the code was asked for on. */
    remember: z.boolean().default(false),
    ...ClientKind,
  }),
]);
export type SignInCodeConfirm = z.infer<typeof SignInCodeConfirm>;
export type SignInCodeConfirmInput = z.input<typeof SignInCodeConfirm>;

/**
 * Change the address the account signs in with (`PUT /api/me/email`,
 * PLAN-2026-10-28-by-email §3.5): the new address and the current password.
 * Nothing changes until the new address's link is used.
 */
export const EmailChangeRequest = z.object({
  email: Email,
  /** The current password, as changing it asks (B-244): any a password may be. */
  password: z.string().min(1).max(256),
});
export type EmailChangeRequest = z.infer<typeof EmailChangeRequest>;
export type EmailChangeInput = z.input<typeof EmailChangeRequest>;

/** A change waiting on its link: where the link went, and until when it works. */
export const EmailChangePending = z.object({
  to: z.string(),
  expiresAt: z.string(),
});
export type EmailChangePending = z.infer<typeof EmailChangePending>;

/** The link from the new address (`POST /api/auth/email/change`). */
export const EmailChangeConfirm = z.object({
  token: z.string().min(16).max(200),
});
export type EmailChangeConfirm = z.infer<typeof EmailChangeConfirm>;
export type EmailChangeConfirmInput = z.input<typeof EmailChangeConfirm>;

/** What the link did: the address the account signs in with now. */
export const EmailChanged = z.object({
  to: z.string(),
});
export type EmailChanged = z.infer<typeof EmailChanged>;

/**
 * The undo from the old address (`POST /api/auth/email/undo`): its token and a
 * new password, since whoever made the change knew the old one. Signs this
 * device in, as a reset does, and every other one out.
 */
export const EmailChangeUndo = z.object({
  token: z.string().min(16).max(200),
  password: NewPassword,
  ...ClientKind,
});
export type EmailChangeUndo = z.infer<typeof EmailChangeUndo>;
export type EmailChangeUndoInput = z.input<typeof EmailChangeUndo>;

/** What confirming said: done now, or done already by an earlier press of the same link. */
export const EmailConfirmed = z.object({
  confirmed: z.literal(true),
  already: z.boolean(),
});
export type EmailConfirmed = z.infer<typeof EmailConfirmed>;

/**
 * Changing the password while signed in (`PUT /api/me/password`, A6, B-244).
 *
 * `current` is the password as it is now, because a session alone is not
 * enough to change the one thing that gets you back in: a phone left unlocked
 * at a party would otherwise be an account somebody else now owns. It is
 * bounded like any password and has no minimum of its own, since an older
 * account's password may predate today's rule; whether it is RIGHT is the
 * operation's to say, and saying so is a refusal, not a parse failure.
 *
 * `signOutOthers` defaults to true for B-58's reason: the usual reason to
 * change a password is the thought that somebody else knows it. The device
 * making the request stays signed in either way.
 */
export const PasswordChange = z
  .object({
    current: z.string().min(1, 'Type the password you have now.').max(256),
    next: NewPassword,
    signOutOthers: z.boolean().default(true),
  })
  .refine((b) => b.next !== b.current, {
    path: ['next'],
    message: 'That is the password you have now. Pick a new one.',
  });
export type PasswordChange = z.infer<typeof PasswordChange>;
export type PasswordChangeInput = z.input<typeof PasswordChange>;

/**
 * Closing an account for good.
 *
 * THE PASSWORD, as the big sites ask before closing an account
 * (PLAN-2026-10-31-deleting-an-account, DIRECTION A28). The server checks it
 * beside the hash before deleting anything, because `DELETE /api/me` is one
 * line of `curl` away and the account does not come back. Bounded like any
 * password, with no minimum of its own: an older account's may predate
 * today's rule, and whether it is RIGHT is the operation's to say.
 *
 * `confirm`, the display name typed back, was the confirmation until
 * 2026-10-31. It proved somebody was paying attention, not that they owned
 * the account: a phone left signed in at a party could close it. Still
 * accepted, since the contract only adds, and no longer read.
 */
export const DeleteAccountRequest = z.object({
  password: z.string().min(1, 'Type your password to confirm.').max(256),
  confirm: z.string().trim().max(40).optional(),
});
export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequest>;
export type DeleteAccountInput = z.input<typeof DeleteAccountRequest>;

export const ProfileUpdate = z.object({
  displayName: z.string().trim().min(1).max(40),
  avatar: AvatarRef,
  /**
   * The account's zone (B-181). Optional on a `PUT` because it came after the
   * other two: absent leaves it as it is, so a client written before it
   * existed cannot clear a zone it never showed.
   */
  timeZone: TimeZone.optional(),
});
export type ProfileUpdate = z.infer<typeof ProfileUpdate>;
export type ProfileUpdateInput = z.input<typeof ProfileUpdate>;

/**
 * Who the request is from, resolved once per request from the session cookie.
 *
 * Every handler takes this rather than re-reading the cookie, so there is one
 * place that turns a token into a person and one place to get it wrong.
 * `null` is the honest shape for a signed-out request: a visitor has no
 * identity at all, not an anonymous one (ACCOUNTS.md, and the reason there is
 * no guest account).
 */
export const Principal = z.object({
  userId: z.string().min(1),
  role: z.enum(['user', 'admin']),
  /** The session row this request authenticated against, so it can be revoked. */
  sessionId: z.string().min(1),
  /** When this session stops being valid, ISO 8601. */
  expiresAt: z.string().datetime(),
  /**
   * The account's zone, which every time the server writes for this request is
   * told in (B-181). Read with the user row the session already loads, so it
   * costs no query. Null for an account made before the field, which reads a
   * league's times in the league's zone until it picks one; optional so a
   * principal built by hand in a test need not name it.
   */
  timeZone: z.string().nullable().optional(),
});
export type Principal = z.infer<typeof Principal>;

/** The zone a time is told in for `principal`: theirs, or `fallback` until they have one. */
export function displayZone(principal: Principal | null | undefined, fallback: string): string {
  return principal?.timeZone ?? fallback;
}

/** A signed-out request has no principal. Not an anonymous one — none. */
export type MaybePrincipal = Principal | null;
